const moment = require('moment-timezone');
const { lessonStartInstant, resolveLessonOccurrences } = require('./lessonOccurrences');
const { isFinalizedAttendance } = require('./lessonAttendance');
const { getSettingLogicalId, normalizeLessonTypes } = require('./appSettings');
const { decorateRoleWithPerson } = require('./personProfiles');
const { hasPermission, isTeacherScoped, canTeacherAccessLessonRegister } = require('./rbac');

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object || {}, key);
const classIdFor = lessonId => String(lessonId || '').replace(/-\d+$/, '');
const validDate = date => typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date) && moment.utc(date, 'YYYY-MM-DD', true).isValid();
const error = code => Object.assign(new Error(code), { code, statusCode: 409 });

function attendanceState(occurrence, register, now) {
  const details = (register && register.lessonDetails) || {};
  if (details.lessonStatus === 'cancelled' || details.attendanceStatus === 'cancelled') return 'cancelled';
  if (isFinalizedAttendance(register)) return 'checked';
  const duration = Number(occurrence.durationMinutes);
  if (!Number.isFinite(duration) || duration <= 0) throw error('PAYROLL_INVALID_DURATION');
  const start = lessonStartInstant(occurrence.date, occurrence.startTime, occurrence.timeZone || 'Europe/Warsaw').getTime();
  const current = new Date(now).getTime();
  if (!Number.isFinite(current)) throw error('PAYROLL_INVALID_CLOCK');
  if (current < start) return 'future';
  return current < start + duration * 60000 ? 'ongoing' : 'missing';
}

function allParticipantsCancelled(occurrence, register) {
  if (!occurrence.rosterKnown || !occurrence.participants.length) return false;
  const saved = new Map(((register && register.students) || []).map(student => [student.id, student]));
  return occurrence.participants.every(participant => {
    const student = saved.get(participant.id);
    return student && ['cancelledOnTime', 'cancelledLate'].includes(student.participationStatus) &&
      !(student.cancellation && student.cancellation.withdrawnAt);
  });
}

function groupBy(rows, key) {
  const index = new Map();
  rows.forEach(row => {
    const id = key(row);
    if (!index.has(id)) index.set(id, []);
    index.get(id).push(row);
  });
  return index;
}

// Build once for a school-wide request and reuse across its teacher projections.
// Maps retain duplicate rows so ambiguous source identity remains visible.
function createPayrollLessonIndexes(sources) {
  const collections = ['classes', 'students', 'people', 'lessonregister', 'classstudents', 'settings'];
  const schoolIds = new Set(['']);
  const byCollection = new Map(collections.map(name => {
    const rows = (sources[name] || []).filter(Boolean);
    rows.forEach(row => { if (row.schoolId) schoolIds.add(row.schoolId); });
    return [name, groupBy(rows, row => row.schoolId || '')];
  }));
  const bySchool = new Map();
  schoolIds.forEach(schoolId => {
    const scoped = name => [...(byCollection.get(name).get('') || []), ...(schoolId ? byCollection.get(name).get(schoolId) || [] : [])];
    const classes = groupBy(scoped('classes'), row => row.id);
    const students = groupBy(scoped('students'), row => row.id);
    const people = groupBy(scoped('people'), row => row.id);
    const registers = groupBy(scoped('lessonregister').map(row => ({ ...row, lessonId: row.lessonId || row.id })), row => row.lessonId);
    const registersByClass = groupBy([...registers.keys()], classIdFor);
    const memberships = groupBy(scoped('classstudents'), row => row.schoolClassId);
    const settings = scoped('settings').filter(row => getSettingLogicalId(row) === 'lessonTypes');
    const types = groupBy(settings.flatMap(setting => setting.types || []), row => String(row.classTypeId || row.id || '').trim().toUpperCase());
    const normalizedTypes = new Map();
    types.forEach((rows, id) => {
      try { normalizedTypes.set(id, normalizeLessonTypes({ types: [rows[0]] }).types[0]); } catch (_) { normalizedTypes.set(id, null); }
    });
    const classesByTeacher = new Map();
    const registersByTeacher = new Map();
    const add = (index, teacherId, id) => {
      if (!teacherId) return;
      if (!index.has(teacherId)) index.set(teacherId, new Set());
      index.get(teacherId).add(id);
    };
    classes.forEach((rows, id) => rows.forEach(row => add(classesByTeacher, row.teacherId, id)));
    registers.forEach((rows, lessonId) => rows.forEach(row => {
      const schoolClassId = classIdFor(lessonId);
      const schoolClass = (classes.get(schoolClassId) || [])[0];
      const teacherId = (row.lessonDetails || {}).teacherId || (schoolClass && schoolClass.teacherId);
      add(registersByTeacher, teacherId, lessonId);
      add(classesByTeacher, teacherId, schoolClassId);
    }));
    bySchool.set(schoolId, { classes,
      students,
      people,
      registers,
      registersByClass,
      memberships,
      settings,
      types,
      normalizedTypes,
      classesByTeacher,
      registersByTeacher });
  });
  return { bySchool, membershipsKnown: Array.isArray(sources.classstudents) };
}

/** Pure monthly projection. Financial values and saved registers remain caller-owned. */
function resolvePayrollLessons({ schoolId, teacherId, month, sources, calendars = [], now, protectedLessonIds = [], auth, indexes }) {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month || '')) throw error('PAYROLL_INVALID_MONTH');
  if (!Number.isFinite(new Date(now).getTime())) throw error('PAYROLL_INVALID_CLOCK');
  const sourceIndexes = indexes || createPayrollLessonIndexes(sources);
  const { classes, students, people, registers, registersByClass, memberships, settings, types, normalizedTypes, classesByTeacher, registersByTeacher } =
    sourceIndexes.bySchool.get(schoolId) || sourceIndexes.bySchool.get('');
  const timeline = calendars.filter(row => row && (!row.schoolId || row.schoolId === schoolId));
  const range = { fromDate: `${month}-01`, tillDate: moment.utc(`${month}-01`).endOf('month').format('YYYY-MM-DD') };
  const occurrences = [];
  const problems = [];
  const emittedProblems = new Set();
  const processed = new Set();
  const problem = (code, lessonId, schoolClassId, extra = {}) => {
    const item = { code, lessonId, schoolClassId, blocking: true, ...extra };
    const key = JSON.stringify(item);
    if (!emittedProblems.has(key)) { problems.push(item); emittedProblems.add(key); }
  };
  const first = (index, id, code, lessonId, schoolClassId) => {
    const rows = index.get(id) || [];
    if (rows.length > 1) problem(code, lessonId, schoolClassId, { id });
    return rows[0];
  };
  const zoneFor = date => {
    const year = Number(String(date).slice(0, 4)) - (String(date).slice(5) < '09-01' ? 1 : 0);
    const published = timeline.filter(calendar => calendar.schoolYearStart === year && calendar.published !== false &&
      (!calendar.status || ['published', 'completed', 'active'].includes(String(calendar.status).toLowerCase())) &&
      new Date(calendar.effectiveAt).getTime() <= new Date(now).getTime());
    return published.length ? published[0].timeZone : 'Europe/Warsaw';
  };
  const relevantDate = date => !date || !/^\d{4}-\d{2}/.test(date) || date.startsWith(`${month}-`);
  const classTouchesMonth = schoolClass => !validDate(schoolClass.fromDate) || (schoolClass.isRecurring && schoolClass.tillDate
    ? schoolClass.fromDate <= range.tillDate && schoolClass.tillDate >= range.fromDate : relevantDate(schoolClass.fromDate));
  const teacherClasses = [...(classesByTeacher.get(teacherId) || [])];
  const teacherRegisters = [...(registersByTeacher.get(teacherId) || [])];
  teacherClasses.forEach(schoolClassId => {
    const rows = classes.get(schoolClassId) || [];
    if (rows.length > 1 && rows.some(row => row.teacherId === teacherId && classTouchesMonth(row))) {
      problem('PAYROLL_DUPLICATE_CLASS', `${schoolClassId}-0`, schoolClassId);
    }
  });
  teacherRegisters.forEach(lessonId => {
    const rows = registers.get(lessonId);
    const schoolClassId = classIdFor(lessonId);
    const schoolClass = (classes.get(schoolClassId) || [])[0];
    if (rows.length > 1 && rows.some(row => ((row.lessonDetails || {}).teacherId || (schoolClass && schoolClass.teacherId)) === teacherId &&
      relevantDate((row.lessonDetails || {}).date))) problem('PAYROLL_DUPLICATE_LESSON', lessonId, schoolClassId);
  });

  function append(base, schoolClass, register, timingProblem) {
    const details = (register && register.lessonDetails) || {};
    const owner = details.teacherId || (schoolClass && schoolClass.teacherId);
    if (owner !== teacherId || (base.calendarDisposition === 'excluded' && !timingProblem)) return;
    const { lessonId } = base;
    const schoolClassId = (schoolClass && schoolClass.id) || classIdFor(lessonId);
    const date = base.effectiveDate;
    if (!relevantDate(date)) return;
    if ((registers.get(lessonId) || []).length > 1) problem('PAYROLL_DUPLICATE_LESSON', lessonId, schoolClassId);
    if ((classes.get(schoolClassId) || []).length > 1) problem('PAYROLL_DUPLICATE_CLASS', lessonId, schoolClassId);
    const classTypeId = String(details.classTypeId || (schoolClass && schoolClass.classTypeId) || '').trim().toUpperCase();
    if (settings.length > 1) problem('PAYROLL_DUPLICATE_SETTING', lessonId, schoolClassId, { id: 'lessonTypes' });
    if ((types.get(classTypeId) || []).length > 1) problem('PAYROLL_DUPLICATE_LESSON_TYPE', lessonId, schoolClassId, { id: classTypeId });
    const type = normalizedTypes.get(classTypeId);
    let rawDuration = (classTypeId.match(/\d+$/) || [])[0];
    if (types.has(classTypeId)) rawDuration = type && type.durationMinutes;
    if (hasOwn(details, 'durationMinutes')) rawDuration = details.durationMinutes;
    const parsedDuration = Number(rawDuration);
    const durationMinutes = Number.isFinite(parsedDuration) && parsedDuration > 0 ? parsedDuration : null;
    if (durationMinutes === null) problem('PAYROLL_INVALID_DURATION', lessonId, schoolClassId);

    const classMemberships = schoolClass && !schoolClass.deleted && !schoolClass.archived ? memberships.get(schoolClassId) || [] : [];
    const savedStudents = register && Array.isArray(register.students) ? register.students : [];
    const rosterKnown = isFinalizedAttendance(register) || Boolean(schoolClass && !schoolClass.deleted && !schoolClass.archived && sourceIndexes.membershipsKnown) ||
      Boolean(register && Array.isArray(register.students));
    if (!rosterKnown) problem('PAYROLL_UNKNOWN_ROSTER', lessonId, schoolClassId);
    const participantRows = isFinalizedAttendance(register) ? savedStudents : [
      ...classMemberships.map(membership => ({ id: membership.studentId, ...(membership.studentName || {}) })),
      ...savedStudents,
    ];
    const participantGroups = groupBy(participantRows, row => row.id);
    const savedGroups = groupBy(savedStudents, row => row.id);
    const membershipGroups = groupBy(classMemberships, row => row.studentId);
    groupBy(classMemberships, row => row.id).forEach((rows, id) => {
      if (id && rows.length > 1) problem('PAYROLL_DUPLICATE_MEMBERSHIP', lessonId, schoolClassId, { id });
    });
    const participants = [...participantGroups].map(([id, rows]) => {
      if (!id) problem('PAYROLL_INVALID_PARTICIPANT', lessonId, schoolClassId);
      if ((savedGroups.get(id) || []).length > 1) problem('PAYROLL_DUPLICATE_PARTICIPANT', lessonId, schoolClassId, { id });
      if ((membershipGroups.get(id) || []).length > 1) problem('PAYROLL_DUPLICATE_MEMBERSHIP', lessonId, schoolClassId, { id });
      const saved = (savedGroups.get(id) || [])[0] || rows[0];
      const student = first(students, id, 'PAYROLL_DUPLICATE_STUDENT', lessonId, schoolClassId) || saved;
      const personId = student.personId || saved.personId;
      const person = personId && first(people, personId, 'PAYROLL_DUPLICATE_PERSON', lessonId, schoolClassId);
      const profile = decorateRoleWithPerson({ ...saved, ...student }, person);
      return { id,
        label: `${profile.firstName || ''} ${profile.lastName || ''}`.trim(),
        isGuest: Boolean(saved.isGuest || saved.guest),
        isMakeup: Boolean(saved.isMakeup) };
    });
    const accessibleClass = schoolClass && !schoolClass.deleted && !schoolClass.archived;
    const canAccessAttendance = Boolean(accessibleClass && auth && auth.id && auth.schoolId === schoolId && hasPermission(auth, 'attendance.read') &&
      (!isTeacherScoped(auth) || canTeacherAccessLessonRegister(auth, register, schoolClass)));
    const occurrence = { schoolClassId,
      lessonId,
      classTypeId,
      baseIndex: base.baseIndex,
      originalDate: base.originalDate,
      date,
      startTime: base.effectiveStartTime,
      nominalDayOfWeek: register ? details.nominalDayOfWeek || '' : (schoolClass && schoolClass.dayOfWeek) || '',
      nominalStartTime: register ? details.nominalStartTime || '' : (schoolClass && schoolClass.startTime) || '',
      durationMinutes,
      timeZone: zoneFor(date),
      teacherId: owner,
      classSchedule: accessibleClass ? { dayOfWeek: schoolClass.dayOfWeek, startTime: schoolClass.startTime } : null,
      participants,
      rosterKnown,
      hasRegister: Boolean(register),
      calendarDisposition: base.calendarDisposition,
      canAccessAttendance };
    try {
      if (timingProblem) throw error(timingProblem);
      // Finalized/cancelled attendance does not excuse unusable source dates for financial identity.
      lessonStartInstant(date, occurrence.startTime, occurrence.timeZone);
      // Participant cancellation completes payroll verification without finalizing attendance
      // or changing the financial distinction between client and school cancellations.
      occurrence.attendanceState = allParticipantsCancelled(occurrence, register) ? 'cancelled' : attendanceState(occurrence, register, now);
    } catch (failure) {
      const code = failure.code && failure.code.startsWith('PAYROLL_') ? failure.code : 'PAYROLL_INVALID_LESSON_DATE';
      problem(code, lessonId, schoolClassId, failure.field ? { field: failure.field } : {});
      occurrence.attendanceState = 'unknown';
    }
    occurrences.push(occurrence);
  }

  teacherClasses.forEach(schoolClassId => {
    const classRows = classes.get(schoolClassId) || [];
    const schoolClass = classRows[0];
    if (!schoolClass || schoolClass.deleted || schoolClass.archived) return;
    const ids = registersByClass.get(schoolClassId) || [];
    const classRegisters = ids.map(id => registers.get(id)[0]);
    if (schoolClass.teacherId !== teacherId && !classRegisters.some(register => (register.lessonDetails || {}).teacherId === teacherId)) return;
    const invalidRegisters = new Map();
    const validRegisters = classRegisters.filter(register => {
      const details = register.lessonDetails || {};
      try {
        lessonStartInstant(details.date || schoolClass.fromDate, details.startTime || schoolClass.startTime);
        return true;
      } catch (_) {
        invalidRegisters.set(register.lessonId || register.id, register);
        return false;
      }
    });
    try {
      const resolved = resolveLessonOccurrences({ schoolClass,
        registers: validRegisters,
        memberships: memberships.get(schoolClassId) || [],
        calendars: timeline,
        now,
        protectedLessonIds });
      resolved.forEach(base => {
        // Remember the full series before month filtering: an undated old register
        // attached to a known occurrence must not reappear as an orphan every month.
        const invalidRegister = invalidRegisters.get(base.lessonId);
        processed.add(base.lessonId);
        if (invalidRegister) {
          const details = invalidRegister.lessonDetails || {};
          append(
            { ...base, effectiveDate: details.date || base.effectiveDate, effectiveStartTime: details.startTime || base.effectiveStartTime },
            schoolClass,
            invalidRegister,
            'PAYROLL_INVALID_LESSON_DATE',
          );
        } else append(base, schoolClass, base.lessonRegister);
      });
    } catch (failure) {
      // Preserve independently valid history when class/calendar data cannot resolve its series.
      if (schoolClass.teacherId === teacherId && classTouchesMonth(schoolClass)) {
        problem('PAYROLL_INVALID_LESSON_DATE', `${schoolClassId}-0`, schoolClassId, failure.field ? { field: failure.field } : {});
      }
    }
  });

  teacherRegisters.forEach(lessonId => {
    if (processed.has(lessonId)) return;
    const rows = registers.get(lessonId);
    const register = rows[0];
    const details = register.lessonDetails || {};
    const schoolClassId = classIdFor(lessonId);
    const schoolClass = (classes.get(schoolClassId) || [])[0];
    if ((details.teacherId || (schoolClass && schoolClass.teacherId)) !== teacherId || !relevantDate(details.date)) return;
    const baseIndexMatch = String(lessonId).match(/-(\d+)$/);
    const base = { lessonId,
      baseIndex: baseIndexMatch ? Number(baseIndexMatch[1]) : null,
      originalDate: null,
      effectiveDate: details.date,
      effectiveStartTime: details.startTime || (schoolClass && schoolClass.startTime),
      calendarDisposition: 'unconfigured' };
    try {
      if (!validDate(details.date)) throw error('PAYROLL_INVALID_LESSON_DATE');
      // A one-off adapter applies exactly the same calendar policy to orphan historical records.
      const adaptedId = `${lessonId}-0`;
      const resolved = resolveLessonOccurrences({ schoolClass: { id: lessonId,
        fromDate: details.date,
        dayOfWeek: moment.utc(details.date).format('ddd').toUpperCase(),
        startTime: base.effectiveStartTime,
        isRecurring: false },
      registers: [{ ...register, lessonId: adaptedId }],
      calendars: timeline,
      range,
      now,
      protectedLessonIds: protectedLessonIds.includes(lessonId) ? [adaptedId] : [] });
      if (resolved.length) base.calendarDisposition = resolved[0].calendarDisposition;
      append(base, schoolClass, register);
    } catch (_) { append(base, schoolClass, register, 'PAYROLL_INVALID_LESSON_DATE'); }
  });
  occurrences.sort((left, right) => `${left.date || ''} ${left.startTime || ''} ${left.lessonId}`.localeCompare(`${right.date || ''} ${right.startTime || ''} ${right.lessonId}`));
  problems.sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right)));
  return { occurrences, problems };
}

module.exports = { attendanceState, resolvePayrollLessons, createPayrollLessonIndexes };
